// v2 mine — a vertical shaft under a timber gallows headframe: a dark shaft
// opening in a timber collar at ground level, two tall front legs with raking
// back braces carrying a head platform and a big winch (sheave) wheel, the hoist
// rope dropping to a kibble bucket rising out of the shaft, a hand windlass at
// the back, an ore tub, a spoil heap and a little glowing pit lantern hung on a
// leg. Ported 1:1 from age-of-mcp's v2/mine.ts, using the library's shared
// `foundationSlab` and `scaffold` helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, spin, EFFECT_LOOP, type Part } from "../../lib";

const TIMBER = "#6e4d2c";   // headframe timber
const WOOD   = "#7a5a30";   // windlass drum
const DARK   = "#161313";   // the shaft opening
const IRON   = "#3c3f45";   // sheave wheel
const ROPE   = "#3a2c1c";
const CART   = "#4a4036";   // kibble + ore tub
const ORE    = "#9a8a6a";   // ore chunks
const ROCK   = "#6a6660";   // spoil
const EMBERG = "#ffd24a";   // lantern glow

const BASE = 0.14;          // foundation slab top
const LEG_X = 0.15;         // headframe leg spacing
const LEG_Z = 0.28;         // headframe legs' z

// The shaft: a dark opening framed by a square timber collar.
function shaftCollar(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.26, 0.05, 0.26), color: DARK,
      position: [0, BASE + 0.025, 0.06] },
    { geometry: new THREE.BoxGeometry(0.42, 0.07, 0.08), color: TIMBER,
      position: [0, BASE + 0.035, 0.24] },
    { geometry: new THREE.BoxGeometry(0.42, 0.07, 0.08), color: TIMBER,
      position: [0, BASE + 0.035, -0.12] },
    { geometry: new THREE.BoxGeometry(0.08, 0.07, 0.28), color: TIMBER,
      position: [-0.17, BASE + 0.035, 0.06] },
    { geometry: new THREE.BoxGeometry(0.08, 0.07, 0.28), color: TIMBER,
      position: [0.17, BASE + 0.035, 0.06] },
  ];
}

// The gallows frame: two front legs (`legH` tall) plus, when `full`, the
// raking back braces, the cross tie, the head platform and bearing blocks.
function gallowsFrame(legH: number, full: boolean): Part[] {
  const parts: Part[] = [];
  for (const sx of [-1, 1] as const) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.06, legH, 0.06), color: TIMBER,
      position: [sx * LEG_X, BASE + legH / 2, LEG_Z],
    });
  }
  parts.push({
    geometry: new THREE.BoxGeometry(0.36, 0.05, 0.06), color: TIMBER,
    position: [0, 0.62, LEG_Z],
  });
  if (full) {
    for (const sx of [-1, 1] as const) {
      parts.push({
        geometry: new THREE.BoxGeometry(0.05, 1.12, 0.05), color: TIMBER,
        position: [sx * LEG_X, 0.62, -0.035], rotation: [0.55, 0, 0],
      });
    }
    parts.push(
      { geometry: new THREE.BoxGeometry(0.40, 0.07, 0.20), color: TIMBER,
        position: [0, 1.17, 0.20] },
      { geometry: new THREE.BoxGeometry(0.05, 0.08, 0.05), color: DARK,
        position: [-0.05, 1.23, 0.10] },
      { geometry: new THREE.BoxGeometry(0.05, 0.08, 0.05), color: DARK,
        position: [0.05, 1.23, 0.10] },
    );
  }
  return parts;
}

// The sheave wheel atop the frame, the hoist rope and the kibble bucket
// rising out of the shaft. Sheave, rope and kibble are named so the "active"
// animation can wind the kibble up out of the shaft.
function hoist(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.12, 0.12, 0.045, 8), color: IRON,
      position: [0, 1.30, 0.10], rotation: [0, 0, Math.PI / 2], name: "sheave" },
    { geometry: new THREE.CylinderGeometry(0.035, 0.035, 0.09, 6), color: DARK,
      position: [0, 1.30, 0.10], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.92, 4), color: ROPE,
      position: [0, 0.76, 0.06], name: "hoist-rope" },
    { geometry: new THREE.CylinderGeometry(0.09, 0.065, 0.13, 7), color: CART,
      position: [0, 0.235, 0.06], name: "kibble" },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: ORE,
      position: [0, 0.31, 0.06], rotation: [0.4, 1.1, 0], name: "kibble" },
  ];
}

// The hand windlass at the back: two posts, the drum and a crank arm.
function windlass(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.05, 0.34, 0.05), color: TIMBER,
      position: [-0.18, BASE + 0.17, -0.30] },
    { geometry: new THREE.BoxGeometry(0.05, 0.34, 0.05), color: TIMBER,
      position: [0.18, BASE + 0.17, -0.30] },
    { geometry: new THREE.CylinderGeometry(0.055, 0.055, 0.30, 6), color: WOOD,
      position: [0, 0.42, -0.30], rotation: [0, 0, Math.PI / 2], name: "windlass" },
    { geometry: new THREE.BoxGeometry(0.025, 0.10, 0.025), color: DARK,
      position: [0.20, 0.47, -0.30], name: "windlass" },
  ];
}

// Ore tub, spoil heap and the glowing pit lantern hung on the east leg.
function yard(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.09, 0.07, 0.14, 7), color: CART,
      position: [0.31, BASE + 0.07, 0.30] },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: ORE,
      position: [0.31, BASE + 0.16, 0.30], rotation: [0.2, 0.7, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.04, 0), color: ORE,
      position: [0.36, BASE + 0.12, 0.24], rotation: [0.8, 1.9, 0.3] },
    { geometry: new THREE.ConeGeometry(0.13, 0.12, 6), color: ROCK,
      position: [-0.31, BASE + 0.06, 0.28] },
    { geometry: new THREE.BoxGeometry(0.02, 0.02, 0.06), color: DARK,
      position: [0.18, 0.54, 0.31] },
    { geometry: new THREE.BoxGeometry(0.035, 0.045, 0.035), color: EMBERG,
      position: [0.18, 0.50, 0.33] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), ...shaftCollar(), ...scaffold(0.82, 0.6)];
  if (s === 2) {
    return [foundationSlab(0.84), ...shaftCollar(), ...gallowsFrame(0.66, false),
      ...scaffold(0.82, 1.0)];
  }
  return [
    foundationSlab(0.84),
    ...shaftCollar(),
    ...gallowsFrame(1.0, true),
    ...hoist(),
    ...windlass(),
    ...yard(),
  ];
}

export const mineV2 = defineModel({
  id: "mine-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Mine (v2)",
  tags: ["building","mine","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly mine.",
  description: "A pit-head over a mountain seam that extracts ore on a slow timer.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      // The winding gear at work: the sheave and windlass turn while the kibble
      // rises out of the shaft on its shortening rope (wound about the rope's
      // top end at the sheave, y = 1.22), then sinks back.
      id: "active",
      label: "Winding ore",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        spin("sheave", [0, 1.30, 0.10], { axis: "x", turns: 2 }),
        spin("windlass", [0, 0.42, -0.30], { axis: "x", turns: 2 }),
        {
          target: "kibble",
          pivot: [0, 0.235, 0.06],
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: EFFECT_LOOP / 2, value: [0, 0.34, 0], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "hoist-rope",
          pivot: [0, 1.22, 0.06],
          scale: [
            { t: 0, value: [1, 1, 1] },
            { t: EFFECT_LOOP / 2, value: [1, 0.63, 1], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [1, 1, 1], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
  stations: [
    { id: "bench", position: [0, 0.14, 0.44], facing: Math.PI },
  ],
  interactions: [
    { id: "work", label: "Working the seam", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
